import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  IconButton,
  Collapse,
  VStack,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon, ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import Delete from "@root/assets/icons/delete-02.svg";
import { EditSvg } from "@root/assets/icons";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "@root/assets/icons/CheckIcon";
import { useCategoryContext } from "@root/context/admin-categories";
import { PAGINATION_DEFAULTS } from "@root/constants";
import { debounce } from "@root/utils";
import { Paginate } from "@root/components/pagination";
import { ModalDelete } from "@root/components/modal-delete";
import SearchInput from "@root/components/common/SearchInput";

export const CourseCategoryManagement = () => {
  const [addingSubCategoryId, setAddingSubCategoryId] = useState<number | null>(
    null
  );
  const [newSubCategory, setNewSubCategory] = useState("");
  const { t } = useTranslation("common");
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const {
    fetchCategories,
    categoriesData,
    setCategoriesData,
    addCategory,
    newCategory,
    setNewCategory,
    page,
    setPage,
    hoveredId,
    setHoveredId,
    addNewCategory,
    setAddNewCategory,
    editingId,
    setEditingId,
    editingValue,
    setEditingValue,
    search,
    setSearch,
    pageCount,
    addCategoryLoading,
    getCategoriesLoading,
    updateCategoryLoading,
    deleteCategoryLoading,
    updateCategory,
    deleteCategory,
  } = useCategoryContext();

  const handleApiCall = (pageParam: any, searchParam?: any) => {
    fetchCategories({
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (pageParam - 1) * PAGINATION_DEFAULTS.PAGESIZE,
      search: searchParam ? searchParam : search,
    });
  };

  useEffect(() => {
    handleApiCall(page);
  }, []);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    handleApiCall(newPage, search);
  };

  const handleToggle = (id: number) => {
    setCategoriesData((prev: any) => ({
      count: prev.count,
      rows: prev?.rows?.map((cat: any) =>
        cat?.id === id ? { ...cat, expanded: !cat?.expanded } : cat
      ),
    }));
  };

  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      setPage(1);
      handleApiCall(page, searchValue);
    }, 500),
    []
  );

  const handleSearch = (e: any) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleAddCategory = () => {
    const payload = {
      title: newCategory,
      parentId: 0,
    };
    addCategory(payload);
  };

  // const handleDeleteCategory = (cat: any) => {
  //   const id = cat?.id;
  //   deleteCategory(id);
  // };

  const handleDeleteCategory = () => {
    deleteCategory(deleteId);
    setIsDeleteModal(false);
    setDeleteId("");
  };

  const handleSaveEdit = (cat: any) => {
    const id = cat?.id;
    const payload = {
      title: editingValue,
      parentId: 0,
    };
    updateCategory(id, payload);
  };

  const handleAddSubCategory = (parentId: number) => {
    const payload = {
      title: newSubCategory,
      parentId: parentId,
    };
    addCategory(payload);
    setAddingSubCategoryId(null);
    setNewSubCategory("");
  };

  const renderSubCategoryInput = (parentId: number) => {
    return (
      <Flex gap={2} align="center" mt={2}>
        <Input
          placeholder={t("categoryManagement.enterSubCategory")}
          value={newSubCategory}
          onChange={(e) => setNewSubCategory(e.target.value)}
          width="100%"
          size="sm"
          bg="white"
          borderRadius="12px"
          border="1px solid #CDD6DC"
          onClick={(e) => e.stopPropagation()}
        />
        {addCategoryLoading ? (
          <Spinner size="sm" />
        ) : (
          <CheckIcon
            width={5}
            cursor="pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleAddSubCategory(parentId);
            }}
          />
        )}
        <Image
          src={Delete}
          width={4}
          ml={1}
          color="red.400"
          cursor="pointer"
          onClick={(e) => {
            e.stopPropagation();
            setAddingSubCategoryId(null);
            setNewSubCategory("");
          }}
        />
      </Flex>
    );
  };

  const renderCategoryName = (cat: any) => {
    if (editingId === cat?.id) {
      return (
        <>
          <Input
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            size="sm"
            mr={2}
            width={"100%"}
            onClick={(e) => e.stopPropagation()}
          />
          {renderCheckIconUpdate(cat)}
        </>
      );
    } else {
      return (
        <>
          <Text
            fontWeight={cat?.expanded ? "bold" : 500}
            flex={1}
            color={cat?.expanded ? "#1819B4" : "#2F2F34"}
          >
            {cat?.title}
          </Text>
          {hoveredId === cat?.id && (
            <>
              <EditSvg
                color={"gray"}
                mr={2}
                title={t("categoryManagement.edit")}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(cat?.id);
                  setEditingValue(cat?.title);
                }}
                style={{ cursor: "pointer" }}
              />
              {renderDeleteIcon(cat)}
            </>
          )}
        </>
      );
    }
  };

  const renderDeleteIcon = (cat: any) => {
    // if (deleteCategoryLoading) {
    //   return <Spinner />;
    // }
    return (
      <Image
        src={Delete}
        width={5}
        ml={3}
        color="red.400"
        onClick={(e) => {
          e.stopPropagation();
          setIsDeleteModal(true);
          setDeleteId(cat?.id);
        }}
        title={t("categoryManagement.delete")}
        style={{ cursor: "pointer" }}
      />
    );
  };

  const renderCheckIconUpdate = (cat: any) => {
    if (updateCategoryLoading) {
      return <Spinner />;
    }
    return (
      <CheckIcon
        width={6}
        cursor={"pointer"}
        onClick={(e) => {
          e.stopPropagation();
          handleSaveEdit(cat);
        }}
      />
    );
  };

  const renderCheckIcon = () => {
    if (addCategoryLoading) {
      return <Spinner />;
    }
    return (
      <CheckIcon width={6} cursor={"pointer"} onClick={handleAddCategory} />
    );
  };

  const renderAddNewCategory = () => {
    if (addNewCategory)
      return (
        <Flex gap={2} align="center" mt={4}>
          <Input
            placeholder={t("categoryManagement.enterCourseCategory")}
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            width={"100%"}
            bg="white"
            borderRadius={"12px"}
            border={"1px solid #CDD6DC"}
          />
          {renderCheckIcon()}
          <Image
            src={Delete}
            width={5}
            ml={1}
            color="red.400"
            cursor={"pointer"}
            onClick={() => setAddNewCategory(false)}
          />
        </Flex>
      );
  };

  const renderSubCategoryName = (sub: any) => {
    if (editingId === sub?.id) {
      return (
        <>
          <Input
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            size="sm"
            mr={2}
            width={"100%"}
            onClick={(e) => e.stopPropagation()}
          />
          {renderCheckIconUpdate(sub)}
        </>
      );
    } else {
      return (
        <>
          <Text flex={1} color="#2F2F34" fontSize="14px">
            {sub?.title}
          </Text>
          {hoveredId === sub?.id && (
            <>
              <EditSvg
                color={"gray"}
                mr={2}
                title={t("categoryManagement.edit")}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(sub?.id);
                  setEditingValue(sub?.title);
                }}
                style={{ cursor: "pointer" }}
              />
              {renderDeleteIcon(sub)}
            </>
          )}
        </>
      );
    }
  };

  const renderCategoriesData = () => {
    if (getCategoriesLoading) {
      return (
        <Flex align="center" justify="center" minH="200px">
          <Spinner size={"lg"} />
        </Flex>
      );
    }
    if (!categoriesData?.rows?.length) {
      return (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.500" textAlign="center" mt={4}>
            {t("categoryManagement.noCategoriesFound")}
          </Text>
        </Flex>
      );
    }

    return (
      <VStack
        align="stretch"
        spacing={1}
        mb={8}
        maxH={"600px"}
        overflowY={"auto"}
      >
        {categoriesData?.rows?.map((cat: any) => (
          <Box key={cat.id} bg={"transparent"} borderRadius="lg">
            <Flex
              align="center"
              px={2}
              py={2}
              _hover={{ bg: "#1819B414", borderRadius: "12px" }}
              cursor={"pointer"}
              onClick={() => handleToggle(cat?.id)}
              onMouseEnter={() => setHoveredId(cat?.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <IconButton
                aria-label={cat?.expanded ? "Collapse" : "Expand"}
                icon={
                  cat?.expanded ? <ChevronDownIcon /> : <ChevronRightIcon />
                }
                variant="ghost"
                size="sm"
                mr={2}
              />
              {renderCategoryName(cat)}
            </Flex>
            <Collapse in={cat?.expanded} animateOpacity>
              <Box pl={10}>
                {cat?.children?.map((sub: any) => (
                  <Flex
                    key={sub.id}
                    align="center"
                    px={2}
                    py={2}
                    _hover={{ bg: "#1819B414", borderRadius: "12px" }}
                    cursor="default"
                    onMouseEnter={() => setHoveredId(sub?.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    {renderSubCategoryName(sub)}
                  </Flex>
                ))}
                {addingSubCategoryId === cat.id ? (
                  renderSubCategoryInput(cat.id)
                ) : (
                  <Button
                    leftIcon={<AddIcon />}
                    color="#1819B4"
                    variant="link"
                    fontWeight="bold"
                    size="sm"
                    mt={2}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddingSubCategoryId(cat?.id);
                    }}
                  >
                    {t("categoryManagement.addSubCategory")}
                  </Button>
                )}
              </Box>
            </Collapse>
          </Box>
        ))}
      </VStack>
    );
  };

  return (
    <Box bg="#FFFFFF" minH="100vh">
      <Box mb={6}>
      <SearchInput
        placeholder={t("categoryManagement.searchPlaceholder")}
        width="600px"
          value={search}
          onChange={handleSearch}
        />
      </Box>
      <Box mb={4}>
        <Button
          leftIcon={<AddIcon />}
          color="#1819B4"
          variant="link"
          fontStyle={"italic"}
          mb={2}
          onClick={() => setAddNewCategory(true)}
        >
          {t("categoryManagement.addCategory")}
        </Button>
        {/* Render Input for add new category */}
        {renderAddNewCategory()}
        {renderCategoriesData()}
      </Box>
      <ModalDelete
        isOpen={isDeleteModal}
        onClose={() => {
          setIsDeleteModal(false);
        }}
        onConfirm={() => handleDeleteCategory()}
        title={t("categoryManagement.removeTitle")}
        description={t("categoryManagement.removeDescription")}
        chatCountDescription={t("categoryManagement.removeConfirm")}
        deleteButtonTitle={t("categoryManagement.Delete")}
        cancelButtonTitle={t("categoryManagement.cancel")}
      />
      <Paginate
        page={page}
        count={pageCount}
        pageSize={PAGINATION_DEFAULTS.PAGESIZE}
        onPageChange={handlePageChange}
        margin={1}
        variant="ghost"
      />
    </Box>
  );
};

export default CourseCategoryManagement;
